(() => {
  const indexes = new Map();
  const batches = new Map();
  const initialCards = new Map();
  const TTL = 30000;
  const enabled = url => url.pathname.endsWith('/search') && !['price-ascending','price-descending'].includes(url.searchParams.get('sort_by'));
  const compare = (a,b) => Number(a.sale)-Number(b.sale) || b.launched-a.launched;
  const remember = (cache,key,value) => {
    cache.set(key,{value,time:Date.now()});
    if (cache.size>20) cache.delete(cache.keys().next().value);
    return value;
  };
  const cached = (cache,key) => {
    const entry=cache.get(key);
    return entry && Date.now()-entry.time<TTL ? entry.value : undefined;
  };
  const checkAbort = signal => {if (signal?.aborted) throw new DOMException('Cancelled','AbortError');};
  async function html(url,signal) {
    checkAbort(signal);
    const response=await fetch(url,{signal,credentials:'same-origin'});
    if (!response.ok) throw Error('Search ranking unavailable');
    return new DOMParser().parseFromString(await response.text(),'text/html');
  }
  function indexUrl(url, details = false) {
    const endpoint=new URL(url);
    for (const key of [...endpoint.searchParams.keys()]) {
      if (!key.startsWith('filter.') && !['q','type','options[prefix]','options[unavailable_products]','_fd','pb'].includes(key)) endpoint.searchParams.delete(key);
    }
    endpoint.searchParams.set('type','product');
    endpoint.searchParams.set('filter.v.availability','1');
    endpoint.searchParams.set('section_id',details ? 'leavys-search-ranking' : 'leavys-search-order');
    endpoint.searchParams.sort();
    return endpoint;
  }
  async function index(url,signal,theme,details = false) {
    const endpoint=indexUrl(url,details), key=theme+'|'+endpoint.href;
    const hit=cached(indexes,key);
    if (hit) {checkAbort(signal);return hit;}
    const load=async page=>{
      const target=new URL(endpoint);if(page>1) target.searchParams.set('page',page);
      const doc=await html(target,signal);
      const node=doc.querySelector('[data-ls-ranking]');
      if (!node) throw Error('Missing ranking index');
      const result=JSON.parse(node.textContent);
      if (String(result.theme)!==String(theme) || !Array.isArray(result.items) || result.pages>4) throw Error('Search changed');
      if (!details && result.pages===1) {
        const template=doc.querySelector('template[data-ls-initial-cards]');
        if (template) result.cards=template.innerHTML;
      }
      return result;
    };
    const first=await load(1);
    const remaining=await Promise.all(Array.from({length:Math.max(0,first.pages-1)},(_,i)=>load(i+2)));
    if (remaining.some(page=>page.count!==first.count)) throw Error('Search changed while loading');
    const items=[...first.items,...remaining.flatMap(page=>page.items)];
    if (items.length!==first.count || new Set(items.map(item=>item.id)).size!==items.length) throw Error('Incomplete search results');
    checkAbort(signal);
    items.sort(compare);
    initialCards.delete(key);
    if (first.cards) {
      const template=document.createElement('template');template.innerHTML=first.cards;
      const found=[...template.content.querySelectorAll('.nf-card')];
      const firstPage=items.slice(0,24);
      if (found.length===firstPage.length && found.every((card,i)=>card.dataset.productId===String(firstPage[i].id) && card.dataset.sizeMatch==='true')) {
        remember(initialCards,key,first.cards);
      }
    }
    return remember(indexes,key,items);
  }
  async function cards(items,url,signal,theme) {
    if (!items.length) return [];
    const endpoint=new URL(url.pathname,url.origin);
    endpoint.searchParams.set('section_id','leavys-ranked-cards');
    endpoint.searchParams.set('type','product');
    endpoint.searchParams.set('q',items.map(item=>item.handle).join(',')+'~'+url.searchParams.getAll('filter.v.option.size').join(','));
    const key=theme+'|'+endpoint.href;
    let source=cached(batches,key);
    if (!source) {
      const doc=await html(endpoint,signal), container=doc.querySelector('[data-ls-ranked-cards]');
      if (!container || container.dataset.theme!==String(theme)) throw Error('Search theme changed');
      source=container.innerHTML;
      remember(batches,key,source);
    }
    checkAbort(signal);
    const template=document.createElement('template');template.innerHTML=source;
    const found=[...template.content.querySelectorAll('.nf-card')];
    if (found.length!==items.length || found.some((card,i)=>card.dataset.productId!==String(items[i].id) || card.dataset.sizeMatch!=='true')) throw Error('Stock changed while loading');
    return found;
  }
  async function apply(root,url,size,signal,preparedIndex) {
    if (!Number(root.dataset.nfResultCount)) return false;
    const items=await (preparedIndex || index(url,signal,root.dataset.nfThemeId));
    if (items.length!==Number(root.dataset.nfResultCount)) throw Error('Search count changed');
    const start=(Math.max(1,Number(url.searchParams.get('page'))||1)-1)*24;
    const page=items.slice(start,start+size);
    const existing=new Map([...root.querySelectorAll('.nf-card')].map(card=>[card.dataset.productId,card]));
    const source=cached(initialCards,root.dataset.nfThemeId+'|'+indexUrl(url).href);
    if (source) {
      const template=document.createElement('template');template.innerHTML=source;
      for (const card of template.content.querySelectorAll('.nf-card')) {
        if (!existing.has(card.dataset.productId)) existing.set(card.dataset.productId,card);
      }
    }
    const missing=page.filter(item=>!existing.has(String(item.id)));
    const pending=[];
    for (let i=0;i<missing.length;i+=20) pending.push(cards(missing.slice(i,i+20),url,signal,root.dataset.nfThemeId));
    for (const card of (await Promise.all(pending)).flat()) existing.set(card.dataset.productId,card);
    checkAbort(signal);
    const orderedCards = page.map(item=>existing.get(String(item.id)));
    // Ranking can bring previously offscreen cards into the first visible row.
    // Set priorities before inserting that row, and keep the rest lazy loaded.
    orderedCards.forEach((card, index) => {
      const image = card.querySelector('.nf-card__image img');
      if (!image) return;
      image.setAttribute('loading', index < 3 ? 'eager' : 'lazy');
      image.setAttribute('fetchpriority', index === 0 ? 'high' : 'auto');
    });
    root.querySelector('[data-nf-grid]').replaceChildren(...orderedCards);
    root.dataset.lsRanked='true';
    const option=root.querySelector('option[value="relevance"]');
    if (option) option.textContent='Recommended';
    return true;
  }
  const node=(tag,text,className)=>{const el=document.createElement(tag);if(text)el.textContent=text;if(className)el.className=className;return el;};
  async function suggestions(url,signal,theme) {
    const items=await index(url,signal,theme,true);
    return items.slice(0,4).map(item=>{
      const link=node('a',null,'ls-product-link');link.href=item.url;
      Object.assign(link.dataset,{productId:String(item.id),productType:item.type,variantId:String(item.variant),nfItemName:item.title,nfBrand:item.brand,nfVariant:String(item.variant),nfVariantName:item.variantName,nfCountry:item.country,nfPrice:String(item.price),nfCurrency:item.currency});
      if (item.image) {const image=node('img');image.src=item.image;image.alt='';image.width=60;image.height=60;link.append(image);}
      const body=node('span'),price=node('span',item.money,'ls-product-price'+(item.compare?' is-sale':''));
      if(item.compare)price.append(node('s',item.compare));
      body.append(node('span',item.title,'ls-product-title'),node('span',item.department,'ls-product-department'),price);link.append(body);
      return link;
    });
  }
  // One early request may be shared by the first results render. It never
  // shares a consumer's cancellation signal, and is isolated by query and theme.
  let warmup;
  const warmKey = (url,theme) => theme+'|'+indexUrl(url).href;
  function warm(url,theme) {
    if (!enabled(url) || !url.searchParams.get('q')) return;
    const key=warmKey(url,theme);
    if (warmup?.key===key) return;
    const promise=index(url,undefined,theme);
    promise.catch(()=>{});
    warmup={key,promise,time:Date.now()};
  }
  async function prepared(url,signal,theme,details=false) {
    checkAbort(signal);
    const hit=!details && warmup?.key===warmKey(url,theme) && Date.now()-warmup.time<TTL ? warmup : null;
    if (!hit) return index(url,signal,theme,details);
    const items=await hit.promise;
    checkAbort(signal);
    return items;
  }
  window.LeavysSearchRanking={enabled,apply,suggestions,index:prepared,warm};
})();
